import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const euro = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const STATUS = { processing: "En cours", accepted: "Accepté", refused: "Refusé", failed: "Échoué", uncertain: "Non confirmé (à vérifier)" };
const newKey = () => `rf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function History({ o }) {
  if (!o.refunds?.length) return null;
  return (
    <ul data-testid={`admin-order-refunds-${o.id}`} className="mt-1 space-y-0.5 text-xs">
      {o.refunds.map((r) => (
        <li key={r.key} className={r.status === "accepted" ? "text-sky-300" : r.status === "processing" ? "text-[var(--muted)]" : "text-red-300"}>
          {new Date(r.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {euro(r.amount)} · {STATUS[r.status] || r.status}{r.error ? ` — ${r.error}` : ""}
        </li>
      ))}
    </ul>
  );
}

export function RefundBox({ o, onDone }) {
  const remaining = Math.round(((o.amount_paid ?? o.amount) - (o.amount_refunded || 0)) * 100) / 100;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(remaining));
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newKey);
  const canRefund = ["paid", "partially_refunded"].includes(o.payment_status) && remaining > 0;
  const submit = async () => {
    const v = Math.round(parseFloat(String(amount).replace(",", ".")) * 100) / 100;
    if (!(v > 0) || v > remaining) return toast.error(`Montant entre 0,01 € et ${euro(remaining)}`);
    if (!window.confirm(`Confirmer le remboursement de ${euro(v)} à ${o.customer_name || "la cliente"} ?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/refund`, { amount: v, idempotency_key: key });
      toast.success(`Remboursement de ${euro(v)} accepté`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
      onDone?.();
    } finally {
      setKey(newKey());
      setBusy(false);
    }
  };
  return (
    <div>
      {o.amount_refunded ? <span data-testid={`admin-order-refunded-total-${o.id}`} className="block text-xs text-sky-300">Remboursé : {euro(o.amount_refunded)} · reste {euro(remaining)}</span> : null}
      <History o={o} />
      {canRefund && !open ? (
        <button data-testid={`admin-order-refund-${o.id}`} onClick={() => setOpen(true)} className="mt-1 rounded-full border border-[var(--line)] px-3 py-1 text-xs hover:border-[var(--gold)]">Rembourser</button>
      ) : null}
      {canRefund && open ? (
        <div data-testid={`admin-order-refund-form-${o.id}`} className="mt-2 space-y-2 rounded-sm border border-[var(--line)] p-2">
          <p className="text-xs">Remboursable : <b>{euro(remaining)}</b></p>
          <input data-testid={`admin-order-refund-amount-${o.id}`} value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="w-24 rounded-sm border border-[var(--line)] bg-[var(--ink)] px-2 py-1 text-xs" />
          <div className="flex gap-2">
            <button data-testid={`admin-order-refund-confirm-${o.id}`} disabled={busy} onClick={submit} className="flex items-center gap-1 rounded-full bg-[var(--gold)] px-3 py-1 text-xs font-semibold text-[var(--ink)] disabled:opacity-50">{busy && <Loader2 className="h-3 w-3 animate-spin" />} Rembourser</button>
            <button data-testid={`admin-order-refund-cancel-${o.id}`} disabled={busy} onClick={() => setOpen(false)} className="text-xs underline">Annuler</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
